import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { cx } from 'class-variance-authority';
import { useInView, useReducedMotion } from 'framer-motion';
import clamp from 'lodash/clamp';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageFormat } from '@/app/components/Image';
import Image from '@/app/components/Image';

import styles from './ImagesSlideOverOnScroll.module.css';

export interface SlideImage {
  path: string;
  alt: MessageKey;
  format: ImageFormat;
}

interface ImagesSlideOverOnScrollProps {
  readonly images: SlideImage[];
  readonly Heading: ReactElement;
  readonly listStyles?: string;
  readonly containerStyles?: string;
  readonly wrapperStyles?: string;
}

const ImagesSlideOverOnScroll = ({
  images,
  Heading,
  listStyles,
  containerStyles,
  wrapperStyles,
}: ImagesSlideOverOnScrollProps): ReactElement => {
  const isMediumScreen = useMediaQuery(`(min-width: ${BREAKPOINTS.medium}px)`);
  const prefersReducedMotion = useReducedMotion();
  const doNotAnimate = prefersReducedMotion ?? !isMediumScreen;

  const [isOnScreen, setIsOnScreen] = useState(false);
  const [state, setState] = useState({
    containerX: 200,
    windowY: 0,
    listWidth: 0,
  });
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { margin: '0px 0px -90px 0px' });

  const scrollContainer = useRef<HTMLDivElement>(null);
  const imagesList = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (isInView) {
      setIsOnScreen(true);
    }
  }, [isInView]);

  // scroll the container when the state changes
  useEffect(() => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollLeft = state.containerX;
    }
  }, [state.containerX]);

  // update state when the user side-scrolls the container
  useEffect(() => {
    if (doNotAnimate && scrollContainer.current) {
      scrollContainer.current.scrollLeft = 250;
      return;
    }

    const updateContainerX = () => {
      setState((prevState) => ({
        ...prevState,
        containerX: scrollContainer.current?.scrollLeft ?? 0,
      }));
    };

    scrollContainer.current?.addEventListener('scroll', updateContainerX, {
      passive: true,
    });

    return () => window.removeEventListener('scroll', updateContainerX);
  }, [doNotAnimate]);

  // update state when the user scrolls down the window
  useEffect(() => {
    if (doNotAnimate) {
      return;
    }

    const updateWindowY = () => {
      const scrollSpeed = 0.7;

      setState(({ containerX, windowY, listWidth }) => {
        const scroll = containerX + (window.scrollY - windowY) * scrollSpeed;
        const maxScroll = listWidth - window.innerWidth;

        return {
          containerX: clamp(scroll, 0, maxScroll),
          windowY: window.scrollY,
          listWidth: imagesList.current?.getBoundingClientRect().width ?? 0,
        };
      });
    };

    if (isOnScreen) {
      setState((prevState) => ({
        ...prevState,
        windowY: window.scrollY,
      }));

      window.addEventListener('scroll', updateWindowY, { passive: true });
    } else {
      window.removeEventListener('scroll', updateWindowY);
    }

    return () => window.removeEventListener('scroll', updateWindowY);
  }, [isOnScreen, doNotAnimate]);

  return (
    <div
      className={cx('relative', wrapperStyles)}
      data-cy="images-slide-over-on-scroll-wrapper"
      ref={containerRef}
    >
      <LayoutGrid>{Heading}</LayoutGrid>
      <div
        className={cx(styles['container'], containerStyles)}
        data-cy="images-slide-over-on-scroll-container"
        ref={scrollContainer}
      >
        <ul
          className={cx(styles['list'], listStyles)}
          data-cy="images-slide-over-on-scroll-list"
          ref={imagesList}
        >
          {images.map(({ path, format, alt }) => (
            <li key={`image-${path}`}>
              <Image
                className={styles['image']}
                draggable="false"
                format={format}
                height={440}
                loading="lazy"
                localizedAlt={alt}
                src={path}
                width={330}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ImagesSlideOverOnScroll;
